// src/theme.js
import { createTheme } from '@mui/material';

const createAppTheme = (mode) => createTheme({
    palette: {
        mode: mode,
        primary: { 
            main: '#8B5CF6', // Purple
            light: '#A78BFA',
            dark: '#7C3AED'
        },
        secondary: { 
            main: '#A855F7', // Light purple
            light: '#C084FC',
            dark: '#9333EA'
        },
        background: { 
            default: mode === 'dark' ? '#000000' : '#FFFFFF',
            paper: mode === 'dark' ? '#111111' : '#FFFFFF'
        },
        text: {
            primary: mode === 'dark' ? '#FFFFFF' : '#000000',
            secondary: mode === 'dark' ? '#A1A1AA' : '#6B7280',
        },
        grey: {
            50: mode === 'dark' ? '#18181B' : '#F9FAFB',
            100: mode === 'dark' ? '#27272A' : '#F3F4F6',
            200: mode === 'dark' ? '#3F3F46' : '#E5E7EB',
            300: mode === 'dark' ? '#52525B' : '#D1D5DB',
            400: mode === 'dark' ? '#71717A' : '#9CA3AF',
            500: mode === 'dark' ? '#A1A1AA' : '#6B7280',
            600: mode === 'dark' ? '#D4D4D8' : '#4B5563',
            700: mode === 'dark' ? '#E4E4E7' : '#374151',
            800: mode === 'dark' ? '#F4F4F5' : '#1F2937',
            900: mode === 'dark' ? '#FAFAFA' : '#111827',
        },
    },
    typography: {
        fontFamily: '-apple-system, BlinkMacSystemFont, "SF Pro Display", "SF Pro Text", "Helvetica Neue", Helvetica, Arial, sans-serif',
        fontFamilySecondary: '-apple-system, BlinkMacSystemFont, "SF Pro Text", "Helvetica Neue", Helvetica, Arial, sans-serif',
        h1: { 
            fontFamily: '-apple-system, BlinkMacSystemFont, "SF Pro Display", "Helvetica Neue", Helvetica, Arial, sans-serif',
            fontWeight: 600,
            color: '#1D1D1F',
            fontSize: '3.5rem',
            lineHeight: 1.1,
            letterSpacing: '-0.02em',
        },
        h2: { 
            fontFamily: '-apple-system, BlinkMacSystemFont, "SF Pro Display", "Helvetica Neue", Helvetica, Arial, sans-serif',
            fontWeight: 600,
            color: '#1D1D1F',
            fontSize: '2.75rem',
            lineHeight: 1.1,
            letterSpacing: '-0.02em',
        },
        h3: { 
            fontFamily: '-apple-system, BlinkMacSystemFont, "SF Pro Display", "Helvetica Neue", Helvetica, Arial, sans-serif',
            fontWeight: 600,
            color: '#1D1D1F',
            fontSize: '2.25rem',
            lineHeight: 1.2,
            letterSpacing: '-0.01em',
        },
        h4: { 
            fontFamily: '-apple-system, BlinkMacSystemFont, "SF Pro Display", "Helvetica Neue", Helvetica, Arial, sans-serif',
            fontWeight: 600,
            color: '#1D1D1F',
            fontSize: '1.75rem',
            lineHeight: 1.2,
        },
        h5: { 
            fontFamily: '-apple-system, BlinkMacSystemFont, "SF Pro Display", "Helvetica Neue", Helvetica, Arial, sans-serif',
            fontWeight: 600,
            color: '#1D1D1F',
            fontSize: '1.375rem',
            lineHeight: 1.3,
        },
        h6: { 
            fontFamily: '-apple-system, BlinkMacSystemFont, "SF Pro Display", "Helvetica Neue", Helvetica, Arial, sans-serif',
            fontWeight: 600,
            color: '#1D1D1F',
            fontSize: '1.125rem',
            lineHeight: 1.3,
        },
        body1: {
            fontFamily: '-apple-system, BlinkMacSystemFont, "SF Pro Text", "Helvetica Neue", Helvetica, Arial, sans-serif',
            color: '#1D1D1F',
            fontSize: '1.125rem',
            lineHeight: 1.5,
        },
        body2: {
            fontFamily: '-apple-system, BlinkMacSystemFont, "SF Pro Text", "Helvetica Neue", Helvetica, Arial, sans-serif',
            color: '#86868B',
            fontSize: '1rem',
            lineHeight: 1.5,
        },
    },
    components: {
        MuiButton: {
            styleOverrides: {
                root: {
                    textTransform: 'none',
                    borderRadius: '980px',
                    fontFamily: '-apple-system, BlinkMacSystemFont, "SF Pro Text", "Helvetica Neue", Helvetica, Arial, sans-serif',
                    fontWeight: 600,
                    fontSize: '1.0625rem',
                    padding: '12px 24px',
                    boxShadow: 'none',
                    '&:hover': {
                        boxShadow: 'none',
                    },
                },
                contained: {
                    backgroundColor: '#007AFF',
                    color: '#FFFFFF',
                    '&:hover': {
                        backgroundColor: '#0056CC',
                    },
                },
                outlined: {
                    borderColor: '#007AFF',
                    color: '#007AFF',
                    borderWidth: '1px',
                    '&:hover': {
                        backgroundColor: 'rgba(0, 122, 255, 0.04)',
                        borderWidth: '1px',
                    },
                },
            },
        },
        MuiCard: {
            styleOverrides: {
                root: {
                    backgroundColor: '#FFFFFF',
                    border: '1px solid #E5E5E7',
                    borderRadius: '18px',
                    boxShadow: '0 4px 16px rgba(0, 0, 0, 0.1)',
                },
            },
        },
        MuiTextField: {
            styleOverrides: {
                root: {
                    '& .MuiOutlinedInput-root': {
                        borderRadius: '12px',
                        backgroundColor: '#F5F5F7',
                        '& fieldset': {
                            borderColor: 'transparent',
                        },
                        '&:hover fieldset': {
                            borderColor: 'transparent',
                        },
                        '&.Mui-focused fieldset': {
                            borderColor: '#007AFF',
                            borderWidth: '2px',
                        },
                    },
                },
            },
        },
    },
});

export default createAppTheme;
